import React from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";

const WhySection = styled.section`
  padding: 80px 0;
  background: linear-gradient(
    135deg,
    var(--background-light) 0%,
    var(--background-medium) 100%
  );
  position: relative;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 80px;
    background: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1000 100' preserveAspectRatio='none'%3E%3Cpath d='M738,99l262-93V0H0v5.6L738,99z' fill='%23ffffff'/%3E%3C/svg%3E")
      no-repeat center top;
    background-size: cover;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 60px;
`;

const SectionTag = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-bottom: 20px;

  .icon {
    width: 8px;
    height: 8px;
    background: var(--secondary-color);
    border-radius: 50%;
  }

  span {
    color: var(--secondary-color);
    font-weight: 600;
    text-transform: uppercase;
    font-size: 0.9rem;
  }
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  color: var(--text-dark);
  margin-bottom: 20px;
  line-height: 1.2;
  font-family: "Inter", sans-serif;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  align-items: start;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

const LeftContent = styled.div`
  background: white;
  border-radius: 20px;
  padding: 40px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
`;

const ContentTitle = styled.h3`
  font-size: 1.8rem;
  color: var(--text-dark);
  margin-bottom: 20px;
  font-family: "Inter", sans-serif;
`;

const ContentDescription = styled.p`
  color: var(--text-light);
  line-height: 1.6;
  margin-bottom: 30px;

  strong {
    color: var(--text-dark);
  }
`;

const RightContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;
`;

const FeatureCard = styled.div`
  background: white;
  border-radius: 20px;
  padding: 30px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  display: flex;
  gap: 20px;
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-5px);
  }
`;

const FeatureIcon = styled.div`
  width: 60px;
  height: 60px;
  background: var(--secondary-color);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.5rem;
  flex-shrink: 0;
`;

const FeatureContent = styled.div`
  flex: 1;
`;

const FeatureNumber = styled.span`
  display: block;
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--secondary-color);
  font-family: "Inter", sans-serif;
  margin-bottom: 8px;
`;

const FeatureTitle = styled.h4`
  font-size: 1.25rem;
  color: var(--text-dark);
  margin-bottom: 12px;
  font-family: "Inter", sans-serif;
`;

const FeatureDescription = styled.p`
  color: var(--text-light);
  line-height: 1.6;
  font-size: 0.95rem;

  strong {
    color: var(--text-dark);
  }
`;

const WhyChooseUs = () => {
  const features = [
    {
      number: "01",
      title: "Wide Selection",
      description:
        "From brand-new one-trip containers to affordable used units, we stock multiple sizes and grades to fit every project and budget.",
      icon: "fas fa-globe",
    },
    {
      number: "02",
      title: "Quality You Can Trust",
      description:
        "Every container is inspected, wind & watertight, and built to last. You can count on durability whether you're storing, shipping, or building.",
      icon: "fas fa-shield-alt",
    },
    {
      number: "03",
      title: "Nationwide Reach",
      description:
        "With our head office in Kentucky and regional partners in New Jersey and Utah, we can deliver quickly and affordably across the U.S.",
      icon: "fas fa-shipping-fast",
    },
  ];

  return (
    <WhySection>
      <Container>
        <SectionHeader>
          <SectionTag>
            <div className="icon"></div>
            <span>Why Baypoint Storage</span>
          </SectionTag>

          <SectionTitle>Strong. Secure. Reliable.</SectionTitle>
        </SectionHeader>

        <ContentGrid>
          <LeftContent>
            <ContentTitle>Why Choose Us?</ContentTitle>
            <ContentDescription>
              At <strong>Baypoint Storage</strong>, we know you have options
              when it comes to buying containers. Here's what sets us apart from
              the competition.
            </ContentDescription>
            <Link to="/about" className="btn btn-secondary">
              Read More
              <i className="fas fa-chevron-right"></i>
            </Link>
          </LeftContent>

          <RightContent>
            {features.map((feature, index) => (
              <FeatureCard key={index}>
                <FeatureIcon>
                  <i className={feature.icon}></i>
                </FeatureIcon>
                <FeatureContent>
                  <FeatureNumber>{feature.number}.</FeatureNumber>
                  <FeatureTitle>{feature.title}</FeatureTitle>
                  <FeatureDescription>{feature.description}</FeatureDescription>
                </FeatureContent>
              </FeatureCard>
            ))}
          </RightContent>
        </ContentGrid>
      </Container>
    </WhySection>
  );
};

export default WhyChooseUs;
